import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import MenuIcon from "../../components/MenuIcons";
import { setUnread } from "../../utils/unread";

// What each kind of notification is about, and the icon shown beside it.
const KINDS = {
    event: { icon: "events", label: "Event" },
    mission: { icon: "missions", label: "Mission" },
    feedback: { icon: "feedback", label: "Mission feedback" },
    request: { icon: "requests", label: "Request to serve" },
    profile: { icon: "profile", label: "Profile" },
};

// "5 minutes ago", "Yesterday, 14:05", "Sat, 10 Oct 2026, 09:00"
const formatWhen = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const minutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

    const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    if (date >= startOfToday) return `Today, ${time}`;
    if (date >= new Date(startOfToday.getTime() - 86400000)) return `Yesterday, ${time}`;

    const day = date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    return `${day}, ${time}`;
};

// The signed-in member's notifications: new events, missions they are to lead,
// feedback that is due and, for admins, new requests to serve.
function Notifications() {
    const [page, setPage] = useState(1);
    const [onlyUnread, setOnlyUnread] = useState(false);
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    // One place for the calls to the API: adds the token and handles a session that has ended.
    const call = useCallback(async (path, method = "GET") => {
        const response = await fetch(`${API_URL}${path}`, {
            method,
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
        });

        if (response.status === 401) {
            navigate("/logout", { state: { from: location } });
            return null;
        }

        if (!response.ok) throw new Error("Request failed");
        return response.json();
    }, [navigate, location]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await call(`/notifications?page=${page}${onlyUnread ? "&unread=1" : ""}`);
            if (!data) return;

            setList(data);
            setUnread(data.unread_count);
            setError("");
        } catch (problem) {
            setError("Your notifications could not be loaded. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [call, page, onlyUnread]);

    useEffect(() => {
        document.title = "Notifications - CFCM Portal";
        load();
    }, [load]);

    // Opening a notification marks it as read and goes to what it is about.
    const open = async (note) => {
        try {
            if (!note.read_at) {
                const data = await call(`/notifications/${note.id}/read`, "POST");
                if (data) setUnread(data.unread_count);
            }

            if (note.url && note.url.startsWith("/")) {
                navigate(note.url);
            } else if (note.url) {
                window.location.assign(note.url);
            } else {
                await load();
            }
        } catch (problem) {
            setError("That notification could not be opened. Please try again.");
        }
    };

    const readAll = async () => {
        try {
            const data = await call("/notifications/read-all", "POST");
            if (data) setUnread(data.unread_count);
            await load();
        } catch (problem) {
            setError("The notifications could not be marked as read. Please try again.");
        }
    };

    const show = (unread) => {
        setOnlyUnread(unread);
        setPage(1);
    };

    const notes = list?.data || [];

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title is-4">Notifications</p>
                    </div>
                </div>
                <div className="level-right">
                    {list && list.unread_count > 0 && (
                        <div className="level-item">
                            <button type="button" className="button primary-outline-button" onClick={readAll}>
                                Mark all as read
                            </button>
                        </div>
                    )}
                </div>
            </nav>

            {error && <Notification type="danger" message={error} onClose={() => setError("")} />}

            <div className="cf-filter-tabs" role="group" aria-label="Which notifications to show">
                <button type="button" className={onlyUnread ? "" : "is-active"} aria-pressed={!onlyUnread} onClick={() => show(false)}>
                    All
                </button>
                <button type="button" className={onlyUnread ? "is-active" : ""} aria-pressed={onlyUnread} onClick={() => show(true)}>
                    Unread{list && list.unread_count > 0 ? ` (${list.unread_count})` : ""}
                </button>
            </div>

            <div className="box cf-note-panel" aria-busy={loading}>
                {!list && loading && <p className="cf-muted cf-note-empty">Loading notifications...</p>}

                {list && notes.length === 0 && (
                    <p className="cf-muted cf-note-empty">
                        {onlyUnread
                            ? "You have read everything."
                            : "Nothing yet. New events, missions you are asked to lead and feedback that is due will appear here."}
                    </p>
                )}

                {notes.length > 0 && (
                    <ul className="cf-note-list">
                        {notes.map((note) => {
                            const kind = KINDS[note.kind] || { icon: "notifications", label: "Notification" };

                            return (
                                <li key={note.id}>
                                    <button
                                        type="button"
                                        className={`cf-note ${note.read_at ? "" : "is-unread"}`}
                                        onClick={() => open(note)}
                                    >
                                        <span className={`cf-note-icon is-${note.kind || "other"}`} title={kind.label}>
                                            <MenuIcon name={kind.icon} />
                                        </span>
                                        <span className="cf-note-body">
                                            <span className="cf-note-title">
                                                {note.title}
                                                {!note.read_at && <span className="cf-note-new">New</span>}
                                            </span>
                                            {note.message && <span className="cf-note-message">{note.message}</span>}
                                            <span className="cf-note-time">
                                                {kind.label} &middot; {formatWhen(note.created_at)}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {list && list.last_page > 1 && (
                    <div className="cf-pager">
                        <button type="button" className="button is-small" disabled={list.current_page <= 1} onClick={() => setPage(list.current_page - 1)}>
                            Previous
                        </button>
                        <span>Page {list.current_page} of {list.last_page}</span>
                        <button type="button" className="button is-small" disabled={list.current_page >= list.last_page} onClick={() => setPage(list.current_page + 1)}>
                            Next
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
}

export default Notifications;
